<template src="./account.html"></template>

<script>
import axios from 'axios'
  import SettingSidebar from '../../common/settingsidebar/settingsidebar'
  import SimpleTable from '../../common/SimpleTable.vue'
  import auth from '../../../helpers/auth'
  import {store} from '../../../store/store'
  import { loadCompanyIntegrations } from '../../../helpers/companyIntegrations'
  import { formatDisplayDate } from '../../../helpers/displayDate'

  export default {
    components: {
      'app-setting-sidebar': SettingSidebar,
      'simple-table': SimpleTable
    },
    computed: {
      xeroIsConnected: function () {
        const integ = this.$store.getters['company/getIntegrations']
        const storeOn = integ && integ.xero === true
        return !!(this.xeroConnection && this.xeroConnection.connected) || storeOn
      },
      isOnSiteUser: function () {
        return this.$store.getters['user/isOnSiteStaff']
      },
      referralCreditLabel: function () {
        const balance = this.referralCreditBalanceCents
        if (balance == null || balance >= 0) return ''
        const amount = (Math.abs(balance) / 100).toFixed(2)
        const currency = (this.referralCreditCurrency || 'AUD').toUpperCase()
        return 'Account credit: $' + amount + ' ' + currency + ' applies to your next invoice'
      }
    },
    data: function () {
      return {
        isDev: process.env.NODE_ENV === 'development',
        settings: {
          business_name: '',
          legal_trading_name: '',
          contact_name: '',
          organisation_type: '',
          phone: '',
          email: '',
          website: '',
          hourly_rate: '',
          callout_fee: '',
          standard_margin_pct: '30',
          price_structure: 1,
          protect_pricing_details: 0,
          pricing_pin: '',
          has_pricing_pin: false,
          addresses: {
            postal_address: {
              address_line_1: '',
              address_line_2: '',
              suburb: '',
              state: '',
              post_code: ''
            },
            street_address: {
              address_line_1: '',
              address_line_2: '',
              suburb: '',
              state: '',
              post_code: ''
            },
            sales_account: ''
          }
        },
        protectPricingOptions: [
          { id: 0, name: 'No' },
          { id: 1, name: 'Yes' }
        ],
        pricingPinError: '',
        users: [],
        // Billing details from /subscription/status (backend aggregates Stripe subscription)
        billing: null,
        subscriptionStatus: null,
        payments: [],
        paymentsLoading: false,
        paymentHistoryHeaders: ['Date', 'Description', 'Amount', 'Status', 'Receipt'],
        referralCode: '',
        referralLink: '',
        referralRows: [],
        referralLoading: false,
        referralHeaders: ['Business', 'Status', 'Date', 'Your credit'],
        referralCreditBalanceCents: null,
        referralCreditCurrency: null,
        match_street_postal: false,
        xeroAccounts: [],
        xeroAccountsScopeWarning: '',
        xeroConnection: {
          connected: false,
          tenant_name: '',
          status: 'disconnected',
          last_error: ''
        },
        discounts: []
      }
    },
    watch: {
      match_street_postal: function () {
        if (this.match_street_postal) {
          this.settings.addresses.street_address = this.settings.addresses.postal_address
        } else {
          this.settings.addresses.street_address = {
            address_line_1: '',
            address_line_2: '',
            suburb: '',
            state: '',
            post_code: ''
          }
        }
      },
      '$route.path': function (path, prev) {
        if (path === '/settings/account' && prev && prev.indexOf('/settings/discount') === 0) {
          this.getDiscounts()
        }
        if (path === '/settings/account' && prev && prev.indexOf('/settings/user') === 0) {
          this.getUsers()
        }
      }
    },
    methods: {
      getDiscounts: function () {
        axios.get('/api/discounts').then(response => {
          this.discounts = Array.isArray(response.data && response.data.discounts) ? response.data.discounts : []
        }).catch(() => {
          this.discounts = []
        })
      },
      editDiscount: function (discount) {
        this.$router.push('/settings/discount/' + discount.id)
      },
      refreshCompanyIntegrations: function () {
        return loadCompanyIntegrations(store)
      },
      getXeroStatus: function () {
        axios.get('/api/integrations/xero/status').then((response) => {
          const payload = response.data && response.data.payload ? response.data.payload : {}
          this.xeroConnection = {
            connected: !!payload.connected,
            tenant_name: payload.tenant_name || '',
            status: payload.status || (payload.connected ? 'connected' : 'disconnected'),
            last_error: payload.last_error || ''
          }
        }).catch(() => {
          this.xeroConnection = { connected: false, tenant_name: '', status: 'disconnected', last_error: '' }
        })
      },
      getSettings: function () {
        axios.get('/api/setting/settings').then(response => {
          if (response.data) {
            this.settings = Object.assign({}, this.settings, response.data, {
              protect_pricing_details: response.data.protect_pricing_details === true || response.data.protect_pricing_details === 1 || response.data.protect_pricing_details === '1' ? 1 : 0,
              has_pricing_pin: !!response.data.has_pricing_pin,
              pricing_pin: /^\d{4}$/.test(String(response.data.pricing_pin || '').trim())
                ? String(response.data.pricing_pin).trim()
                : ''
            })
            if (!this.settings.addresses) {
              this.$set(this.settings, 'addresses', { sales_account: '' })
            } else if (this.settings.addresses.sales_account === undefined || this.settings.addresses.sales_account === null) {
              this.$set(this.settings.addresses, 'sales_account', '')
            }
            this.applyDefaultSalesAccountIfNeeded()
          }
        }).catch(error => {
          console.error('Error loading settings:', error)
          this.errorNotification('Failed to load account settings')
        })
      },
      onProtectPricingSelected: function (payload) {
        const next = payload && (payload.id === 1 || payload.id === '1') ? 1 : 0
        this.$set(this.settings, 'protect_pricing_details', next)
        this.pricingPinError = ''
        if (next === 0) {
          this.$set(this.settings, 'pricing_pin', '')
        }
      },
      onPricingPinInput: function (value) {
        this.$set(this.settings, 'pricing_pin', value)
        this.pricingPinError = ''
      },
      applyDefaultSalesAccountIfNeeded: function () {
        if (!this.settings.addresses) return
        const cur = this.settings.addresses.sales_account
        if (cur !== undefined && cur !== null && String(cur).trim() !== '') return
        if (!this.xeroAccounts || !this.xeroAccounts.length) return
        const exact = this.xeroAccounts.find(function (a) {
          return String(a.name || '').trim().toLowerCase() === 'sales'
        })
        const partial = this.xeroAccounts.find(function (a) {
          return /sales/i.test(String(a.name || ''))
        })
        const pick = exact || partial || this.xeroAccounts[0]
        if (pick) {
          this.$set(this.settings.addresses, 'sales_account', pick.id)
        }
      },
      getBillingStatus: function () {
        axios.get('/subscription/status').then((response) => {
          this.subscriptionStatus = response.data || null
          if (response && response.data && response.data.billing) {
            this.billing = response.data.billing
          } else {
            this.billing = null
          }
        }).catch((error) => {
          console.error('Error loading subscription status:', error)
          this.billing = null
          this.subscriptionStatus = null
        })
        this.getPaymentHistory()
        this.getReferralProgram()
      },
      getReferralProgram: function () {
        this.referralLoading = true
        axios.get('/api/referrals').then((response) => {
          const data = response.data || {}
          this.referralCode = data.referral_code || ''
          this.referralRows = Array.isArray(data.referrals) ? data.referrals : []
          this.referralCreditBalanceCents = typeof data.credit_balance_cents === 'number'
            ? data.credit_balance_cents
            : null
          this.referralCreditCurrency = data.credit_currency || null
          const origin = typeof window !== 'undefined' && window.location
            ? window.location.origin
            : ''
          this.referralLink = this.referralCode && origin
            ? (origin + '/register?ref=' + encodeURIComponent(this.referralCode))
            : ''
        }).catch((error) => {
          console.error('Error loading referral program:', error)
          this.referralCode = ''
          this.referralLink = ''
          this.referralRows = []
          this.referralCreditBalanceCents = null
          this.referralCreditCurrency = null
        }).finally(() => {
          this.referralLoading = false
        })
      },
      copyReferralLink: function () {
        const link = this.referralLink
        if (!link) return
        const done = () => {
          this.successNotification('Referral link copied')
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(link).then(done).catch(() => {
            this.fallbackCopyReferralLink(link, done)
          })
        } else {
          this.fallbackCopyReferralLink(link, done)
        }
      },
      fallbackCopyReferralLink: function (link, done) {
        try {
          const input = document.createElement('input')
          input.value = link
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
          if (typeof done === 'function') done()
        } catch (e) {
          this.errorNotification('Could not copy link')
        }
      },
      formatReferralStatus: function (status) {
        const s = String(status || '').trim().toLowerCase()
        if (s === 'pending') return 'Pending payment'
        if (s === 'rewarded') return 'Rewarded'
        if (s === 'cancelled') return 'Cancelled'
        return s ? (s.charAt(0).toUpperCase() + s.slice(1)) : ''
      },
      formatReferralCredit: function (item) {
        if (!item || item.status !== 'rewarded' || item.referrer_credit_cents == null) return '—'
        return '$' + (Number(item.referrer_credit_cents) / 100).toFixed(2)
      },
      getPaymentHistory: function () {
        this.paymentsLoading = true
        axios.get('/subscription/payments').then((response) => {
          this.payments = Array.isArray(response.data && response.data.payments)
            ? response.data.payments
            : []
        }).catch((error) => {
          console.error('Error loading payment history:', error)
          this.payments = []
        }).finally(() => {
          this.paymentsLoading = false
        })
      },
      formatBillingDate: function (date) {
        return formatDisplayDate(date, '')
      },
      formatPaymentStatus: function (status) {
        const s = String(status || '').trim()
        if (!s) return ''
        return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
      },
      resetOnboarding: function () {
        axios.post('/api/auth/onboarding/reset').then(() => {
          this.$store.commit('user/setUserProfile', Object.assign({}, this.$store.getters['user/getProfile'], { onboarding_completed: false }))
          this.successNotification('Onboarding reset. Redirecting…')
          this.$router.push('/onboarding/welcome')
        }).catch((err) => {
          this.errorNotification((err.response && err.response.data && err.response.data.error) || 'Reset failed')
        })
      },
      saveSettings: function () {
        this.pricingPinError = ''
        const protectOn = Number(this.settings.protect_pricing_details) === 1
        const pin = this.settings.pricing_pin != null ? String(this.settings.pricing_pin).trim() : ''
        if (protectOn && !pin && !this.settings.has_pricing_pin) {
          this.pricingPinError = 'Enter a 4-digit PIN'
          this.errorNotification('A 4-digit PIN is required when protecting pricing details')
          return
        }
        if (pin && !/^\d{4}$/.test(pin)) {
          this.pricingPinError = 'PIN must be exactly 4 digits'
          this.errorNotification('PIN must be exactly 4 digits')
          return
        }

        const payload = Object.assign({}, this.settings, {
          protect_pricing_details: protectOn ? 1 : 0,
          pricing_pin: protectOn ? pin : ''
        })
        // Never send server-only flags back as authoritative writes
        delete payload.has_pricing_pin

        console.log('Saving settings:', JSON.stringify(payload, null, 2));
        axios.put('/api/setting/settings', payload).then(response => {
          this.successNotification('Account settings saved successfully')
          if (protectOn && pin) {
            this.$set(this.settings, 'has_pricing_pin', true)
          }
          if (!protectOn) {
            this.$set(this.settings, 'has_pricing_pin', false)
            this.$set(this.settings, 'pricing_pin', '')
          }
          const profile = this.$store.getters['user/getProfile'] || {}
          this.$store.commit('user/setUserProfile', Object.assign({}, profile, {
            protect_pricing_details: protectOn
          }))
        }).catch(error => {
          console.error('Error saving settings:', error)
          if (error.response && error.response.data) {
            console.error('Error response:', error.response.data)
            const errorMessage = (error.response.data.details || error.response.data.error || 'Failed to save account settings');
            this.errorNotification(errorMessage)
            if (/PIN/i.test(String(errorMessage))) {
              this.pricingPinError = errorMessage
            }
          } else {
            this.errorNotification('Failed to save account settings')
          }
        })
      },
      getUsers: function () {
        axios.get('/api/invitations/company').then(response => {
          if (response.data && Array.isArray(response.data)) {
            this.users = response.data
          }
        }).catch(error => {
          console.error('Error loading users:', error)
        })
      },
      getOwnProfile: function () {
        return axios.get('/api/auth/user').then((response) => {
          const u = response.data && response.data.user ? response.data.user : null
          if (!u) return
          this.users = [{
            membership_id: u.id,
            id: u.id,
            first_name: u.first_name || '',
            last_name: u.last_name || '',
            email: u.email || '',
            type_name: 'On-Site',
            status_name: 'Active',
            status: 'active'
          }]
        })
      },
      xeroConnect: function () {
        axios.post('/api/integrations/xero/connect').then(response => {
          window.location = response.data.payload.url
        }).catch((error) => {
          const msg = error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : 'Failed to start Sign in with Xero'
          this.errorNotification(msg)
        })
      },
      xeroDisconnect: function () {
        axios.delete('/api/integrations/xero/disconnect').then(response => {
          // Optimistically clear local connection state so the UI flips to the
          // disconnected layout immediately, regardless of how quickly the
          // follow-up /status and /company reads come back.
          this.xeroConnection = { connected: false, tenant_name: '', status: 'disconnected', last_error: '' }
          this.xeroAccounts = []
          this.xeroAccountsScopeWarning = ''
          const currentIntegrations = this.$store.getters['company/getIntegrations'] || {}
          store.commit('company/setIntegrations', Object.assign({}, currentIntegrations, { xero: false }))
          this.refreshCompanyIntegrations().then(() => {
            this.getXeroStatus()
            this.successNotification('Xero has been disconnected')
          })
        }).catch((error) => {
          const msg = error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : 'Failed to disconnect Xero'
          this.errorNotification(msg)
        })
      },
      serviceM8Connect: function () {
        axios.get('/api/integrations/servicem8/oauth/authorise')
          .then(response => {
            window.location = response.data.data.url;
          })
          .catch(error => {
            const msg = error && error.response && error.response.data && error.response.data.error
              ? error.response.data.error
              : 'Failed to start ServiceM8 connection'
            this.errorNotification(msg)
          })
      },
      serviceM8Disconnect: function () {
        axios.delete('/api/integrations/servicem8/oauth/disconnect')
          .then(() => {
            return this.refreshCompanyIntegrations()
          })
          .then(() => {
            this.successNotification('ServiceM8 has been disconnected')
          })
          .catch(error => {
            const msg = error && error.response && error.response.data && error.response.data.error
              ? error.response.data.error
              : 'Failed to disconnect ServiceM8'
            this.errorNotification(msg)
          })
      },
      getXeroAccounts: function () {
        axios.get('/api/integrations/xero/accounts').then(response => {
          this.xeroAccounts = []
          const payload = response.data && response.data.payload ? response.data.payload : {}
          this.xeroAccountsScopeWarning = payload.scope_warning ? String(payload.scope_warning) : ''
          const accounts = payload.accounts || []

          for (let i = 0; i < accounts.length; i++) {
            if (accounts[i].Status === 'ACTIVE' && accounts[i].Code > 199 && accounts[i].Code < 300) {
              this.xeroAccounts.push({
                id: String(accounts[i].Code),
                name: accounts[i].Name
              })
            }
          }
          this.applyDefaultSalesAccountIfNeeded()
        }).catch(error => {
          // Silently fail if Xero accounts can't be loaded
          console.error('Error loading Xero accounts:', error)
        })
      },
      inviteUser: function () {
        if (store.getters['user/getSubscription'].name === 'Personal') {
          this.swal({
            title: 'Upgrade required',
            text: 'Please contact us to upgrade your plan to add more users!',
            type: 'info'
          })
        } else {
          this.$router.push('/settings/user/new')
        }
      },
      editUser: function (user) {
        if (this.isOnSiteUser) {
          this.$router.push('/settings/user/me')
          return
        }
        if (!user || !user.membership_id) return
        this.$router.push('/settings/user/' + user.membership_id)
      },
      resendInvite: function (user, event) {
        if (event) event.stopPropagation()
        if (!user || !user.membership_id) return
        axios.post('/api/invitations/' + user.membership_id + '/resend').then(() => {
          this.successNotification('Invitation resent')
        }).catch((err) => {
          const msg = (err.response && err.response.data && err.response.data.error) || 'Failed to resend invitation'
          this.errorNotification(msg)
        })
      },
      revokeInvite: function (user, event) {
        if (event) event.stopPropagation()
        if (!user || !user.membership_id) return
        axios.delete('/api/invitations/' + user.membership_id).then(() => {
          this.successNotification('Invitation revoked')
          this.getUsers()
        }).catch((err) => {
          const msg = (err.response && err.response.data && err.response.data.error) || 'Failed to revoke invitation'
          this.errorNotification(msg)
        })
      },
      initializeAccountPage: function () {
        if (this.$route.query && this.$route.query.xero) {
          const state = String(this.$route.query.xero)
          const message = this.$route.query.message ? String(this.$route.query.message) : ''
          if (state === 'connected') {
            this.successNotification(message || 'Xero connected successfully')
          } else if (state === 'error') {
            this.errorNotification(message || 'Xero connection failed')
          }
        }

        if (this.$route.query && this.$route.query.sm8_connected === '1') {
          this.successNotification('Connected to ServiceM8 successfully')
        }

        this.getSettings()
        if (this.isOnSiteUser) {
          this.getOwnProfile()
        } else {
          this.getUsers()
          this.getBillingStatus()
          this.getDiscounts()
          this.refreshCompanyIntegrations().then(() => {
            this.getXeroStatus()
            this.getXeroAccounts()
          })
        }
        this.$nextTick(() => {
          this.setupAccountTabs()
        })
      },
      setupAccountTabs: function () {
        const $menu = $(this.$el).find('.ui.menu .item')
        if (!$menu.length) return
        $menu.tab()
        const q = this.$route.query || {}
        if (this.isOnSiteUser || q.tab === 'users') {
          $menu.tab('change tab', 'second')
        } else if (q.tab === 'integrations' || q.xero === 'connected' || q.xero === 'error' || q.sm8_connected === '1') {
          $menu.tab('change tab', 'third')
        }
      }
    },
    created: function () {
      /**
       * Component requires authentication, check if user is authenticated
       */
      auth.check(this).then(() => {
        this.initializeAccountPage()
      }).catch(() => {})
    },
    mounted: function () {
      /**
       * Initialise Semantic UI checkbox
       */
      $(this.$el).find('.checkbox').checkbox()

      /**
       * Initialise Semantic UI tooltip
       */
      $(this.$el).find('.tooltip').popup({ inline: true })
    }
  }
</script>

<style scoped>
.pricing-strategy {
  display: flex;
  gap: 16px;
}
.pricing-strategy__card {
  flex: 1;
  padding: 16px 18px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-surface);
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.pricing-strategy__card--selected {
  background: var(--color-selected);
  border-color: var(--color-primary);
  box-shadow: 0 0 0 1px var(--color-primary);
}
.pricing-strategy__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.pricing-strategy__icon {
  width: 32px;
  height: 32px;
  color: var(--color-primary);
}
.pricing-strategy__title {
  font-weight: 600;
  color: var(--color-text);
}
.pricing-strategy__body {
  margin: 4px 0 10px 0;
  color: var(--color-text);
}
.pricing-strategy__card--selected .pricing-strategy__title,
.pricing-strategy__card--selected .pricing-strategy__body,
.pricing-strategy__card--selected .pricing-strategy__callout label {
  color: var(--color-dark-grey) !important;
}
.pricing-strategy__callout label {
  font-weight: 600;
  color: var(--color-text);
}
.pricing-protect-row {
  margin-top: 0.5rem;
}
.pricing-pin-help {
  margin: 8px 0 0;
  color: var(--color-text-muted);
  font-size: 12px;
  line-height: 1.4;
}
.pricing-discounts-header {
  margin-top: 34px !important;
}
.billing-plan-block p {
  margin-top: 0;
  margin-bottom: 0.35em;
}
.billing-plan-block p:last-child {
  margin-bottom: 0;
}
.billing-plan-block .billing-next-invoice-label {
  margin-top: 1.2em;
}
.billing-payment-history-header {
  margin-top: 32px !important;
}
.pricing-discounts-toolbar {
  align-items: center;
}
.pricing-discounts-copy {
  color: var(--color-text);
}
.pricing-discounts-action {
  text-align: right;
}
.pricing-discounts-action a.ui.button {
  margin-left: 0;
}
.pricing-discounts-table tbody tr {
  cursor: pointer;
}
.pricing-discounts-table tbody tr:hover {
  background: var(--color-selected);
}
.pricing-discounts-empty-row {
  cursor: default;
}
.pricing-discounts-empty-row:hover {
  background: transparent !important;
}
.pricing-discounts-table thead tr {
  background: transparent;
}
@media (min-width: 768px) {
  .pricing-discounts-table thead th:last-child,
  .pricing-discounts-table tbody td:last-child {
    text-align: right;
  }
}
.pricing-discount-pill {
  display: inline-block;
  border: 1px dashed #323c47;
  padding: 6px 12px;
  min-width: 110px;
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.empty-state-container {
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 40px 20px;
  text-align: center;
  margin: 0 0 20px 0;
}
.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.empty-state-title {
  font-family: var(--font-semibold);
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 8px 0;
}
.empty-state-description {
  font-family: var(--font-semibold);
  font-size: 14px;
  color: var(--color-text-muted);
  margin: 0;
  line-height: 1.4;
}
@media (max-width: 767px) {
  .pricing-strategy {
    flex-direction: column;
  }
  .pricing-discounts-toolbar .field.pricing-discounts-action {
    width: 100%;
  }
  .pricing-discounts-action {
    text-align: left;
  }
  .pricing-discounts-action a.ui.button {
    float: none !important;
    box-sizing: border-box;
  }
  .pricing-discounts-table th,
  .pricing-discounts-table td {
    text-align: left !important;
  }
}
</style>
